import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Stack,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    FormControlLabel,
    Checkbox,
    Button,
    Radio,
    RadioGroup,
    Typography,
    Divider,
    Alert,
} from '@mui/material';
import useToast from '../../../hooks/useToast';
import {
    useQuestionsContext,
    useSourceHelpers,
} from '../../../context/QuestionsContext';

const DEFAULT_MC_OPTIONS = [
    { id: 'a', text: '' },
    { id: 'b', text: '' },
    { id: 'c', text: '' },
    { id: 'd', text: '' },
];

export default function EditQuestionModal({ open, onClose, question, user }) {
    const toast = useToast();
    const {
        sources = [],
        updateQuestion,
        difficulties,
        questionTypes,
    } = useQuestionsContext();
    const { uniqueSourceTypes, getSourcesByType } = useSourceHelpers();

    const [formData, setFormData] = useState({
        text: '',
        questionType: '',
        difficulty: '',
        correctAnswers: '',
        mcOptions: DEFAULT_MC_OPTIONS,
        mcCorrectAnswerId: null,
        explanation: '',
        hints: '',
        chapter: '',
        sourceType: '',
        sourceId: '',
        isActive: true,
        isNew: true,
        needsReview: false,
    });

    useEffect(() => {
        if (!question) return;

        const isMC = question.questionType === 'MultipleChoice';

        setFormData({
            text: question.text || '',
            questionType: question.questionType || '',
            difficulty: question.difficulty || '',
            // For TextInput: join correctAnswers as comma string
            // For MC: correctAnswers holds the winning option id e.g. 'a'
            correctAnswers: isMC
                ? ''
                : (question.correctAnswers || []).join(', '),
            mcOptions: question.mcOptions?.length
                ? question.mcOptions
                : DEFAULT_MC_OPTIONS,
            mcCorrectAnswerId: isMC
                ? (question.correctAnswers?.[0] ?? null)
                : 'a',
            explanation: question.explanation || '',
            hints: (question.hints || []).join(', '),
            chapter: question.chapter || '',
            sourceType: question.source?.type || '',
            sourceId: question.sourceId?.toString() || '',
            isActive: question.isActive ?? true,
            isNew: question.isNew ?? true,
            needsReview: question.needsReview ?? false,
        });
    }, [question]);

    const filteredSourcesByType = useMemo(() => {
        if (!sources.length) return [];
        const filtered = getSourcesByType(formData.sourceType);
        if (!filtered.length && question?.source?.type) {
            return sources.filter((s) => s.title === question.source.title);
        }
        return filtered;
    }, [sources, formData.sourceType, question, getSourcesByType]);

    const handleTextChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (name, checked) => {
        setFormData((prev) => ({ ...prev, [name]: checked }));
    };

    const handleMcOptionTextChange = (id, value) => {
        setFormData((prev) => ({
            ...prev,
            mcOptions: prev.mcOptions.map((opt) =>
                opt.id === id ? { ...opt, text: value } : opt
            ),
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const isMC = formData.questionType === 'MultipleChoice';

        const submitData = {
            ...question,
            ...formData,
            authorUserId: question.authorUserId,
            lastEditedByUserId: user.userId,
            mcOptions: isMC ? formData.mcOptions : null,
            correctAnswers: isMC
                ? [formData.mcCorrectAnswerId]
                : formData.correctAnswers
                    .split(',')
                    .map((a) => a.trim())
                    .filter(Boolean),
            hints: formData.hints
                ? formData.hints.split(',').map((h) => h.trim()).filter(Boolean)
                : [],
            sourceId: formData.sourceId ? parseInt(formData.sourceId) : 1,
        };

        if (!formData.difficulty) {
            toast.error('Please select a difficulty level before saving.');
            return;
        }
        if (!formData.sourceId) {
            toast.error('Please select a source before saving.');
            return;
        }

        if (isMC) {
            if (!formData.mcCorrectAnswerId) {
                toast.error('Please select the correct answer option before saving.');
                return;
            }
            const emptyOptions = formData.mcOptions.filter((opt) => !opt.text.trim());
            if (emptyOptions.length > 0) {
                toast.error('Please fill in all four answer options before saving.');
                return;
            }
        }

        if (!isMC && !formData.correctAnswers.trim()) {
            toast.error('Please enter at least one correct answer before saving.');
            return;
        }

        updateQuestion(submitData)
            .then(() => {
                toast.success('Question updated successfully!');
                onClose();
            })
           .catch((err) => {
                console.error('Error updating question:', err);
                const message = err?.response?.data?.message;
                toast.error(message || 'Failed to update question. Please check all fields and try again.');
            });
    };

    const isMC = formData.questionType === 'MultipleChoice';

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>Edit Question</DialogTitle>
            <DialogContent dividers>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={3} mt={1}>
                        <TextField
                            label='Question Text'
                            name='text'
                            value={formData.text}
                            onChange={handleTextChange}
                            multiline
                            rows={3}
                            fullWidth
                            required
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth sx={{ minWidth: 200 }}>
                                    <InputLabel>Question Type</InputLabel>
                                    <Select
                                        name='questionType'
                                        value={formData.questionType || ''}
                                        onChange={(e) =>
                                            handleSelectChange('questionType', e.target.value)
                                        }
                                        label='Question Type'
                                    >
                                        {questionTypes?.map(({ id, label }) => (
                                            <MenuItem key={id} value={label}>
                                                {label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth sx={{ minWidth: 200 }}>
                                    <InputLabel>Difficulty</InputLabel>
                                    <Select
                                        name='difficulty'
                                        value={formData.difficulty || ''}
                                        onChange={(e) =>
                                            handleSelectChange('difficulty', e.target.value)
                                        }
                                        label='Difficulty'
                                    >
                                        {difficulties?.map(({ id, label }) => (
                                            <MenuItem key={id} value={label}>
                                                {label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* TextInput: freeform correct answers */}
                        {!isMC && (
                            <TextField
                                label='Correct Answer(s)'
                                name='correctAnswers'
                                value={formData.correctAnswers}
                                onChange={handleTextChange}
                                fullWidth
                                helperText='Separate multiple answers with commas'
                            />
                        )}

                        {/* MultipleChoice: options builder */}
                        {isMC && (
                            <Stack spacing={1}>
                                <Typography variant='subtitle2' color='text.secondary'>
                                    Answer Options
                                </Typography>
                                <Divider />
                                <RadioGroup
                                    value={formData.mcCorrectAnswerId}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            mcCorrectAnswerId: e.target.value,
                                        }))
                                    }
                                >
                                    {formData.mcOptions.map((opt) => (
                                        <Stack
                                            key={opt.id}
                                            direction='row'
                                            alignItems='center'
                                            spacing={1}
                                        >
                                            <FormControlLabel
                                                value={opt.id}
                                                control={<Radio size='small' />}
                                                label=''
                                                sx={{ mr: 0 }}
                                            />
                                            <Typography
                                                variant='body2'
                                                sx={{ minWidth: 20, fontWeight: 'bold' }}
                                            >
                                                {opt.id.toUpperCase()}.
                                            </Typography>
                                            <TextField
                                                size='small'
                                                fullWidth
                                                placeholder={`Option ${opt.id.toUpperCase()}`}
                                                value={opt.text}
                                                onChange={(e) =>
                                                    handleMcOptionTextChange(opt.id, e.target.value)
                                                }
                                            />
                                        </Stack>
                                    ))}
                                </RadioGroup>
                                {!formData.mcCorrectAnswerId && (
                               <Alert 
                                    severity='warning' 
                                    sx={{ 
                                        mt: 1,
                                        backgroundColor: 'rgba(255, 167, 38, 0.1)',
                                        color: '#ffa726',
                                        '& .MuiAlert-icon': {
                                            color: '#ffa726',
                                        }
                                    }}
                                >
                                    Select the radio button next to the correct answer before saving
                                </Alert>
                            )}
                            </Stack>
                        )}

                        <TextField
                            label='Explanation'
                            name='explanation'
                            value={formData.explanation}
                            onChange={handleTextChange}
                            fullWidth
                            multiline
                            rows={3}
                        />

                        <TextField
                            label='Hints'
                            name='hints'
                            value={formData.hints}
                            onChange={handleTextChange}
                            fullWidth
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label='Chapter'
                                    name='chapter'
                                    value={formData.chapter}
                                    onChange={handleTextChange}
                                    fullWidth
                                    sx={{ minWidth: 200 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Stack spacing={2}>
                                    <FormControl fullWidth sx={{ minWidth: 200 }}>
                                        <InputLabel>Source Type</InputLabel>
                                        <Select
                                            name='sourceType'
                                            value={formData.sourceType || ''}
                                            onChange={(e) =>
                                                handleSelectChange('sourceType', e.target.value)
                                            }
                                            label='Source Type'
                                        >
                                            {uniqueSourceTypes?.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth sx={{ minWidth: 200 }}>
                                        <InputLabel>Source Title</InputLabel>
                                        <Select
                                            name='sourceId'
                                            value={formData.sourceId || ''}
                                            onChange={(e) =>
                                                handleSelectChange('sourceId', e.target.value)
                                            }
                                            label='Source Title'
                                        >
                                            {filteredSourcesByType?.map((source) => (
                                                <MenuItem key={source.id} value={source.id}>
                                                    {source.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                            </Grid>
                        </Grid>

                        <Stack direction='row' spacing={3} flexWrap='wrap'>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.isActive}
                                        onChange={(e) =>
                                            handleCheckboxChange('isActive', e.target.checked)
                                        }
                                    />
                                }
                                label='Active'
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.isNew}
                                        onChange={(e) =>
                                            handleCheckboxChange('isNew', e.target.checked)
                                        }
                                    />
                                }
                                label='New'
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.needsReview}
                                        onChange={(e) =>
                                            handleCheckboxChange('needsReview', e.target.checked)
                                        }
                                    />
                                }
                                label='Needs Review'
                            />
                        </Stack>
                    </Stack>
                </form>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color='inherit'>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant='contained' color='primary'>
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
}